import React, { useEffect } from 'react'
import classNames from 'classnames'
import { CheckBox } from '@/atoms/CheckBox'
import { FlexColumn, FlexRow } from '@/atoms/FlexContainers'
import { Image } from '@/atoms/Image'
import { CaptionSM, CaptionXS, Text } from '@/atoms/Text'
import { useCowboyCount } from '@/contexts/CowboyCountContext'
import { useToggle } from '@/hooks/useToggle'
import { useLocale } from '@/utils/LocaleUtil'
import { getBooleanFromLocalStorage, removeFromLocalStorage, writeToLocalStorage } from '@/utils/local-storage'
import { useTranslate } from '@/utils/translate/translate-client'

export const CowboyUpPromo = () => {
  const { t } = useTranslate('tickets')
  const { locale } = useLocale()
  const [isOptedIn, toggleIsOptedIn] = useToggle(false)
  const { cowboyCount, incrementCowboyCount, decrementCowboyCount, isLoading } = useCowboyCount()

  useEffect(() => {
    const isOptedInStorage = getBooleanFromLocalStorage('COWBOY_UP_OPT_IN')
    if (isOptedInStorage && !isOptedIn) {
      toggleIsOptedIn()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!isOptedIn) {
      removeFromLocalStorage('COWBOY_UP_OPT_IN')
    }
    if (isOptedIn) {
      writeToLocalStorage('COWBOY_UP_OPT_IN', true)
    }
  }, [isOptedIn])

  const progressPercent = Math.min(Math.floor((cowboyCount / 5000) * 100), 100)

  const handleToggle = () => {
    const newState = !isOptedIn
    toggleIsOptedIn()

    if (newState) {
      incrementCowboyCount()
    } else {
      decrementCowboyCount()
    }
  }

  return (
    <div className="w-full">
      <div className="flex items-center justify-between text-gray-200">
        <Text color="core-gray-400" className="photon-caption-xs md:photon-caption-sm">
          {t('CowboysUpfortheBigScreen', 'Cowboy Up for the Big Screen')}
        </Text>

        <Text color="core-gray-400" className="photon-caption-xs md:photon-caption-sm">
          {t('CowboysOptedIn', `Cowboys: {{ NumberOfCowboys }}`, {
            NumberOfCowboys: new Intl.NumberFormat(locale).format(cowboyCount),
          })}
        </Text>
      </div>
      <OptInBar percent={progressPercent} className="my-1.5" />
      <FlexColumn className="mt-4 items-center justify-center rounded-lg border border-white/[0.08] bg-core-warning-800 p-2 ">
        <FlexRow className="h-full items-center gap-1">
          <CheckBox
            checked={isOptedIn}
            checkboxSize={24}
            wrapperClassName={'hidden lg:flex'}
            onChange={handleToggle}
            disabled={isLoading}
            uncheckedClassName="!border-white/40 bg-white/10 !w-4 lg:!w-6 !h-2 lg:h-6 !rounded-[6px] cursor-pointer"
            checkedClassName={classNames('!w-4 lg:!w-6 !h-2 lg:h-6 !rounded-[2px] cursor-pointer !bg-warm-03')}
          />
          <CheckBox
            checked={isOptedIn}
            color={'black'}
            checkboxSize={20}
            wrapperClassName={'lg:hidden h-2'}
            onChange={handleToggle}
            disabled={isLoading}
            uncheckedClassName="!border-white/40 bg-white/10 !w-4  !h-2  !rounded-[6px] cursor-pointer"
            checkedClassName={classNames(
              '!w-[20px] !h-[20px] !min-h-2  !min-w-2 !rounded-[6px] cursor-pointer !bg-warm-03',
            )}
          />

          <CaptionSM className={'hidden lg:block'} color={'white'}>
            {' '}
            I&apos;m coming dressed in my cowboy best on opening weekend.
          </CaptionSM>
          <CaptionXS className={'lg:hidden'} color={'white'}>
            {' '}
            I&apos;m coming dressed in my cowboy best on opening weekend.
          </CaptionXS>
        </FlexRow>
      </FlexColumn>
    </div>
  )
}

const OptInBar = ({ percent, className }: { percent: number; className?: string }) => {
  return (
    <div
      className={classNames(
        'w-full pointer-events-none select-none rounded-full bg-white/20 backdrop-blur-[56px] relative',
        className,
      )}
    >
      <div
        className="relative h-[6px] rounded-full bg-warm-03 lg:h-2"
        style={{
          width: `${percent}%`,
        }}
      />
      <div className="absolute top-1/2 -translate-y-1/2" style={{ left: `${percent}%` }}>
        <Image
          src={'/v1745720627/the-last-rodeo/tLR_Photo-Real-Buckle-1200.png'}
          className="h-6 w-auto object-contain"
          width={32}
          height={24}
          alt="Progress indicator"
        />
      </div>
    </div>
  )
}
